import { useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconThumbUp, IconThumbDown, IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { translationApi } from "@services/translationApi";

interface Props {
  translationId: string;
  targetLang: string;
}

export const FeedbackControl: React.FC<Props> = ({ translationId, targetLang }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [sentiment, setSentiment] = useState<"positive" | "negative" | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const start = (value: "positive" | "negative") => {
    setSentiment(value);
    setOpened(true);
  };

  const send = async () => {
    if (!sentiment) return;
    setSubmitting(true);
    try {
      const feedback = comment.trim() ? `${sentiment}: ${comment.trim()}` : sentiment;
      await translationApi.submitFeedback({
        translation_id: translationId,
        target_lang: targetLang,
        feedback,
      });
      setDone(true);
      setOpened(false);
    } catch {
      /* keep popover open so the user can retry */
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Group gap={4} c="green">
        <IconCheck size={14} />
        <Text size="xs">{t("translation.feedbackThanks")}</Text>
      </Group>
    );
  }

  return (
    <Popover opened={opened} onChange={setOpened} position="top-end" withArrow shadow="md" width={260}>
      <Popover.Target>
        <Group gap={4} wrap="nowrap">
          <Text size="xs" c="dimmed">
            {t("translation.helpful")}
          </Text>
          <ActionIcon
            variant={sentiment === "positive" ? "filled" : "subtle"}
            color="green"
            size="sm"
            onClick={() => start("positive")}
            aria-label={t("translation.feedbackGood")}
          >
            <IconThumbUp size={15} />
          </ActionIcon>
          <ActionIcon
            variant={sentiment === "negative" ? "filled" : "subtle"}
            color="red"
            size="sm"
            onClick={() => start("negative")}
            aria-label={t("translation.feedbackBad")}
          >
            <IconThumbDown size={15} />
          </ActionIcon>
        </Group>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            placeholder={t("translation.feedbackPlaceholder")}
            autosize
            minRows={2}
            maxRows={4}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" size="xs" onClick={() => setOpened(false)}>
              {t("common.cancel")}
            </Button>
            <Button size="xs" onClick={send} loading={submitting}>
              {t("translation.send")}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
